import React, { useState } from 'react';
import { useEventListener } from 'hooks/useEventListener';
import { Sun, Moon } from 'lucide-react';
import { Button } from 'components/ui/button';
import {
  applyTheme,
  getStoredThemeMode,
  setStoredThemeMode,
  isValidThemeId,
  DEFAULT_THEME_ID,
  themeHasDarkMode,
  type ThemeMode,
} from 'theme';
import { persistence } from 'services/persistence';

export const ThemeToggle: React.FC = () => {
  const [mode, setMode] = useState<ThemeMode>(getStoredThemeMode);

  const refreshMode = () => setMode(getStoredThemeMode());
  useEventListener('profileUpdated', refreshMode);
  useEventListener('storage', refreshMode);

  const toggle = () => {
    const nextMode: ThemeMode = mode === 'light' ? 'dark' : 'light';

    let themeId = DEFAULT_THEME_ID;
    const parsed = persistence.getJSON<{theme?: string} | null>('eir-user-profile', null);
    if (parsed && isValidThemeId(parsed?.theme)) themeId = parsed.theme;

    if (!themeHasDarkMode(themeId) && nextMode === 'dark') return;

    setStoredThemeMode(nextMode);
    applyTheme(themeId, nextMode);
    setMode(nextMode);
    window.dispatchEvent(new Event('profileUpdated'));
  };

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={toggle}
      title={mode === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
      className="relative overflow-hidden"
    >
      <Sun
        className={`h-4 w-4 transition-all duration-300 ${
          mode === 'light'
            ? 'rotate-0 scale-100 opacity-100'
            : '-rotate-90 scale-0 opacity-0'
        }`}
        style={{ position: mode === 'light' ? 'relative' : 'absolute' }}
      />
      <Moon
        className={`h-4 w-4 transition-all duration-300 ${
          mode === 'dark'
            ? 'rotate-0 scale-100 opacity-100'
            : 'rotate-90 scale-0 opacity-0'
        }`}
        style={{ position: mode === 'dark' ? 'relative' : 'absolute' }}
      />
    </Button>
  );
};
